import * as Icons from '@ant-design/icons';
import { Button, Drawer, Flex, Image, Skeleton, Typography } from 'antd';
import { IComplementaryActivity } from 'interfaces/complementary_activities';
import { getComplementaryActivityFileUrl } from 'services/graduate-enrollment/complementary-activities';
import { useCallback, useEffect, useState } from 'react';
import { useNotification } from '@refinedev/core';

interface ComplementaryActivityFileViewerProps {
  enrollmentId: string;
  activity: IComplementaryActivity;
  open: boolean;
  onClose: () => void;
}

// Exibe o comprovante num drawer, como o DocumentViewer da documentação.
export const ComplementaryActivityFileViewer = ({
  enrollmentId,
  activity,
  open: openDrawer,
  onClose
}: ComplementaryActivityFileViewerProps) => {
  const { open } = useNotification();
  const [fileUrl, setFileUrl] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);

  const loadFile = useCallback(async () => {
    try {
      setLoadingFile(true);
      setFileUrl(
        await getComplementaryActivityFileUrl(enrollmentId, activity.id)
      );
    } catch {
      open?.({
        type: 'error',
        message: 'Ops!',
        description: 'Erro ao recuperar o comprovante. Tente novamente.'
      });
    } finally {
      setLoadingFile(false);
    }
  }, [activity.id, enrollmentId, open]);

  // Busca a URL só ao abrir; `open` do useNotification não é estável.
  useEffect(() => {
    if (openDrawer) {
      loadFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer, enrollmentId, activity.id]);

  const handleCloseDrawer = useCallback(() => {
    setFileUrl('');
    onClose();
  }, [onClose]);

  return (
    <Drawer
      className="!p-0"
      title={
        <Flex vertical>
          <Typography.Title level={4} className="!mb-0">
            Comprovante da AAC
          </Typography.Title>
          <Typography.Text type="secondary" className="font-normal">
            {activity.file_name}
          </Typography.Text>
        </Flex>
      }
      extra={
        <Button
          variant="text"
          color="default"
          icon={<Icons.CloseOutlined />}
          onClick={handleCloseDrawer}
        />
      }
      closable={false}
      closeIcon={false}
      onClose={handleCloseDrawer}
      open={openDrawer}
      width="50%"
    >
      <div className="relative flex items-center justify-center min-h-full">
        {loadingFile ? (
          <Skeleton.Image active className="!w-full h-full min-h-[500px]" />
        ) : (
          fileUrl &&
          (activity.file_name.toLowerCase().endsWith('.pdf') ? (
            <iframe
              src={fileUrl}
              className="absolute top-0 left-0 overflow-y-auto overflow-x-hidden w-full h-full flex-1"
            />
          ) : (
            <Image src={fileUrl} />
          ))
        )}
      </div>
    </Drawer>
  );
};
